import axios from 'axios';
import Cookies from 'js-cookie';
import { Auth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/research';

function getCsrfToken() {
    return Cookies.get('csrftoken') || '';
}

const storageClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable sending cookies for session-based auth
});

storageClient.interceptors.request.use(
    (config) => {
        const csrfToken = getCsrfToken();
        if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
            config.headers['X-CSRFToken'] = csrfToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

export const Storage = {
    put: async (key, file, options = {}) => {
        const { contentType, level = 'public', progressCallback } = options;
        
        const formData = new FormData();
        formData.append('file', file);
        // include the client-generated key so server stores the same identifier
        if (key) {
            formData.append('key', key);
        }
        
        try {
            const response = await storageClient.post('/storage/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                params: { level },
                onUploadProgress: (progressEvent) => {
                    if (progressCallback) {
                        progressCallback({
                            loaded: progressEvent.loaded,
                            total: progressEvent.total,
                        });
                    }
                },
            });
            
            return { key: response.data.key };
        } catch (error) {
            console.error('Storage.put error:', error);
            throw error;
        }
    },

    get: async (key, options = {}) => {
        const { level = 'public', expires = 3600 } = options;
        
        try {
            const response = await storageClient.get('/storage/url/', {
                params: { key, level, expires },
            });
            
            return response.data.url;
        } catch (error) {
            console.error('Storage.get error:', error);
            throw error;
        }
    },

    remove: async (key, options = {}) => {
        const { level = 'public' } = options;
        
        try {
            const response = await storageClient.delete('/storage/delete/', {
                params: { key, level },
            });
            
            return response.data;
        } catch (error) {
            console.error('Storage.remove error:', error);
            throw error;
        }
    },

    list: async (prefix = '', options = {}) => {
        const { level = 'public' } = options;
        
        try {
            const response = await storageClient.post('/storage/list/', {
                prefix,
                level,
            });
            
            return response.data.files;
        } catch (error) {
            console.error('Storage.list error:', error);
            throw error;
        }
    },

    configure: (config) => {
        // Storage configuration
    },

    getPresignedUploadUrl: async (filename, options = {}) => {
        const { contentType = 'application/octet-stream', level = 'public' } = options;
        
        try {
            const response = await storageClient.post('/storage/presigned-upload/', {
                filename,
                contentType,
                level,
            });
            
            return {
                uploadUrl: response.data.uploadUrl,
                key: response.data.key,
            };
        } catch (error) {
            console.error('Storage.getPresignedUploadUrl error:', error);
            throw error;
        }
    },
};

export default Storage;

